import { computed, nextTick, ref, type ComputedRef } from 'vue';
import { parsePhoneNumber } from 'libphonenumber-js';

import type { useAuthStore } from '@/stores/auth';

type AuthStore = ReturnType<typeof useAuthStore>;

type RouteLike = {
  query: Record<string, unknown>;
  params: Record<string, unknown>;
};

type RouterLike = {
  options: {
    history: {
      state: {
        back?: unknown;
      };
    };
  };
};

type ProfileForm = {
  firstname: string;
  lastname: string;
  phoneNumber: string;
};

function getQueryStringInsensitive(
  query: Record<string, unknown>,
  candidates: string[]
): string | undefined {
  for (const key of candidates) {
    const direct = query[key];
    if (typeof direct === 'string') {
      return direct;
    }
  }

  const candidateSet = new Set(candidates.map((key) => key.toLowerCase()));
  for (const [key, value] of Object.entries(query)) {
    if (candidateSet.has(key.toLowerCase()) && typeof value === 'string') {
      return value;
    }
  }

  return undefined;
}

function extractPhoneNumberFromUrl(): string | undefined {
  const url = window.location.href;
  const match = url.match(
    /[?&](phoneNumber|phonenumber|phone|phone_number)=([^&]+)/i
  );
  if (!match?.[2]) {
    return undefined;
  }

  const rawValue = match[2];
  const plusSafe = rawValue.replace(/%2B/gi, '+');

  try {
    return decodeURIComponent(plusSafe);
  } catch (error) {
    return plusSafe;
  }
}

export function useHelpeeSignupBootstrap(params: {
  route: RouteLike;
  router: RouterLike;
  authStore: AuthStore;
  locale: { value: string };
  hexColor: { value: string };
  isIntro: ComputedRef<boolean>;
  goToStep: (
    step: 'intro' | 'profile' | 'phone' | 'criteria' | 'redirect'
  ) => void;
  profileForm: ProfileForm;
}) {
  const initialized = ref(false);
  const isWidget = ref(false);
  const containerWidth = ref(0);
  const flowContainerRef = ref<HTMLElement>();

  const isInFlow = computed(() => !params.isIntro.value);

  const cameFromCampaigns = computed(() => {
    const back = params.router.options.history.state.back;
    return typeof back === 'string' && back.includes('/helpee-campaigns/');
  });

  const computeContainerWidth = async () => {
    await nextTick();

    if (flowContainerRef.value) {
      containerWidth.value = flowContainerRef.value.offsetWidth || 0;
    }
  };

  const applyQueryPrefill = () => {
    const query = params.route.query;

    const firstname = getQueryStringInsensitive(query, [
      'firstname',
      'firstName',
    ]);
    if (firstname) {
      params.profileForm.firstname = firstname;
    }

    const lastname = getQueryStringInsensitive(query, ['lastname', 'lastName']);
    if (lastname) {
      params.profileForm.lastname = lastname;
    }

    const phoneFromUrl = extractPhoneNumberFromUrl();
    const phoneFromQuery = getQueryStringInsensitive(query, [
      'phoneNumber',
      'phonenumber',
      'phone',
      'phone_number',
    ]);

    const rawPhoneNumber = phoneFromUrl || phoneFromQuery;
    if (!rawPhoneNumber) {
      return;
    }

    if (rawPhoneNumber.startsWith('+') && rawPhoneNumber.length > 1) {
      try {
        const tel = parsePhoneNumber(rawPhoneNumber);
        params.profileForm.phoneNumber = tel.formatNational();
        return;
      } catch (error) {
        params.profileForm.phoneNumber = rawPhoneNumber;
        return;
      }
    }

    params.profileForm.phoneNumber = rawPhoneNumber;
  };

  const mount = async () => {
    const widgetQuery = getQueryStringInsensitive(params.route.query, [
      'widget',
    ]);
    if (widgetQuery !== undefined) {
      isWidget.value = true;
    }

    const campaignUuid = params.route.params.campaignUuid;
    if (typeof campaignUuid === 'string' && campaignUuid.length > 0) {
      await params.authStore.getHelpeeSignUpData({
        campaignUuid,
      });
    }

    applyQueryPrefill();

    if (params.authStore.helpeeSignUpData) {
      params.locale.value = params.authStore.helpeeSignUpData.language;
    }

    if (params.authStore.helpeeSignUpData?.primaryColor) {
      params.hexColor.value = params.authStore.helpeeSignUpData.primaryColor;
    }

    initialized.value = true;

    if (cameFromCampaigns.value) {
      params.goToStep('profile');
    }

    await computeContainerWidth();
    window.addEventListener('resize', computeContainerWidth);
  };

  const unmount = () => {
    window.removeEventListener('resize', computeContainerWidth);
  };

  return {
    initialized,
    isWidget,
    containerWidth,
    flowContainerRef,
    isInFlow,
    cameFromCampaigns,
    computeContainerWidth,
    mount,
    unmount,
  };
}
